import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { featured, featuredMain } from '@/mocks/home';
import SectionHead from './SectionHead';

gsap.registerPlugin(ScrollTrigger);

const series = [
  { title: featuredMain.title, body: featuredMain.body, image: featuredMain.image },
  ...featured.map((f) => ({ title: f.title, body: f.body, image: f.image })),
];

const STICKY_QUERY = '(min-width: 1024px)';

export default function Featured() {
  const sectionRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Theme: one trigger, one CSS variable (--nl-t). No pin, no transforms.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;
    const root = document.documentElement;
    let last = -1;
    const setT = (t: number) => {
      const v = Math.round(t * 1000) / 1000;
      if (v === last) return;
      last = v;
      if (v === 0) root.style.removeProperty('--nl-t');
      else root.style.setProperty('--nl-t', String(v));
    };
    const ease = (x: number) => x * x * (3 - 2 * x);
    // Smooth follower: fast wheel jumps are eased over time instead of snapping.
    const proxy = { t: 0 };
    const tween = gsap.quickTo(proxy, 't', {
      duration: 0.6,
      ease: 'power2.out',
      onUpdate: () => setT(proxy.t),
    });
    // Starts as Categories ends (Catalog top near viewport bottom); fully dark
    // after the Catalog pin has moved roughly 1.5 products horizontally.
    const catalog = document.getElementById('catalog') ?? section;
    const st = ScrollTrigger.create({
      id: 'featured-theme',
      trigger: catalog,
      start: 'top 90%',
      end: 'max',
      refreshPriority: -2,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        const px = self.scroll() - self.start;
        const vh = window.innerHeight;
        const inLen = vh * 0.9 + 440; // reach pin start, then ~1.5 products of travel
        const tIn = Math.min(1, Math.max(0, px / inLen));
        // Fade-out measured live from Featured's real position, so it never
        // depends on stale cached offsets (Catalog pin spacer, image loads).
        // Longer exit: starts when Featured's bottom reaches 100% of viewport,
        // done when it is 10% above the top (~1.1 screens of scroll).
        const bottom = section.getBoundingClientRect().bottom;
        const tOut = Math.min(1, Math.max(0, (bottom + vh * 0.1) / (vh * 1.1)));
        tween(ease(Math.max(0, Math.min(tIn, tOut))));
      },
      onLeaveBack: () => tween(0),
    });
    return () => {
      st.kill();
      gsap.killTweensOf(proxy);
      root.style.removeProperty('--nl-t');
    };
  }, []);
  const [active, setActive] = useState(0);
  const [sticky, setSticky] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(STICKY_QUERY);
    const update = () => setSticky(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // One ScrollTrigger per story, no pinning (CSS sticky handles the stage).
  useEffect(() => {
    const list = listRef.current;
    if (!sticky || !list) return undefined;
    const ctx = gsap.context(() => {
      const items = gsap.utils.toArray<HTMLElement>('[data-story]', list);
      items.forEach((el, i) => {
        ScrollTrigger.create({
          trigger: el,
          start: 'top 55%',
          end: 'bottom 55%',
          refreshPriority: -1, // compute after the pinned hero adds its spacer
          onToggle: (self) => { if (self.isActive) setActive(i); },
        });
      });
    }, list);
    ScrollTrigger.refresh();
    return () => ctx.revert();
  }, [sticky]);

  return (
    <section id="featured" ref={sectionRef} className="bg-background-50">
      <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-12 py-24 md:py-32">
        <SectionHead motion={false} label="Featured Components" title="SELECTED SERIES." intro="Form, material and connection — the essentials buyers check first." />

        {sticky ? (
          <div className="grid grid-cols-[55fr_45fr] gap-12 xl:gap-20 mt-14 md:mt-20" data-product-shop>
            <div>
              <div className="sticky top-0 h-screen pt-[72px] flex items-center justify-center">
              <div className="relative r-img w-full aspect-[5/4] max-h-[calc(100vh-9rem)] bg-background-50 overflow-hidden">
                {series.map((s, i) => (
                  <img
                    key={s.title}
                    src={s.image}
                    alt={`${s.title} - NORTHLINE featured series`}
                    title={`${s.title} - NORTHLINE`}
                    aria-hidden={i !== active}
                    className={`absolute inset-0 w-full h-full object-contain object-center transition-opacity duration-[400ms] ease-out motion-reduce:transition-none ${i === active ? 'opacity-100' : 'opacity-0'}`}
                  />
                ))}
              </div>
              </div>
            </div>
            <div ref={listRef} className="flex flex-col">
              {series.map((s, i) => (
                <article key={s.title} data-story className="min-h-[70vh] flex flex-col justify-center last:min-h-[100vh]">
                  <div className={`h-px w-8 mb-6 transition-colors duration-[400ms] ${i === active ? 'bg-primary-500' : 'bg-background-300'}`} />
                  <h3 className={`h-section !text-[40px] xl:!text-[48px] transition-colors duration-[400ms] ${i === active ? 'text-foreground-950' : 'text-foreground-400'}`}>{s.title}</h3>
                  <p className="mt-5 t-body text-foreground-600 max-w-md">{s.body}</p>
                  <a href="#quote" className="mt-8 self-start whitespace-nowrap cursor-pointer inline-flex items-center gap-2 t-body text-foreground-950 hover:text-primary-500 transition-colors">
                    Request this series <i className="ri-arrow-right-line" />
                  </a>
                </article>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-16 md:gap-20 mt-14 md:mt-20" data-product-shop>
            {series.map((s) => (
              <article key={s.title} className="w-full">
                <div className="r-img w-full aspect-[5/4] bg-background-50 overflow-hidden">
                  <img src={s.image} alt={`${s.title} - NORTHLINE featured series`} title={`${s.title} - NORTHLINE`} className="w-full h-full object-contain object-center" />
                </div>
                <h3 className="mt-6 t-feature text-foreground-950">{s.title}</h3>
                <p className="mt-3 t-body text-foreground-600 max-w-lg">{s.body}</p>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}